/**
 * AuthProvider + useAuth().
 *
 * The verified identity/role come from `GET /v1/me` — never from client-side
 * claims. There is NO backend /login; cloud sign-in is Firebase Google popup,
 * and the API client attaches the resulting ID token automatically.
 *
 *  - LOCAL/DEV (no VITE_FIREBASE_*): getMe succeeds tokenless (server injects a
 *    fixed dev reviewer). No sign-in step.
 *  - CLOUD: on mount, getMe may 401 ("not signed in") — that is NOT an error to
 *    surface; we route to /login. After signInWithPopup, Firebase's auth-state
 *    listener re-runs getMe and the token attaches on every request.
 *  - DEMO (no VITE_API_BASE_URL): no real API; a fake operator identity is
 *    provided so the shell renders, alongside the DEMO badge.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  api,
  IS_DEMO,
  type MeResponse,
  type Role,
  setOnUnauthorized,
  setTokenProvider,
} from "../api/client";
import { ApiError, isApiError } from "../api/client";
import {
  AUTH_METHOD,
  type AuthMethod,
  FIREBASE_ENABLED,
  firebaseAuth,
  getIdToken,
  googleSignIn,
  googleSignOut,
  passwordSignIn,
} from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

export type AuthState = {
  /** The server-verified caller, or null when not signed in. */
  user: MeResponse | null;
  /** Convenience alias for `user.role`. */
  role: Role | null;
  /** True while the initial identity check (or a re-check) is in flight. */
  loading: boolean;
  /** A non-auth error from the identity check (auth 401s are NOT errors). */
  error: ApiError | Error | null;
  /**
   * Cloud sign-in. Google method ignores the credentials; password method
   * requires them. No-op (throws) in local/demo mode.
   */
  signIn: (credentials?: { email: string; password: string }) => Promise<void>;
  /** Sign out (Firebase) and clear identity. */
  signOut: () => Promise<void>;
  /** Whether real sign-in is available (cloud / Firebase configured). */
  canSignIn: boolean;
  /** Build-time sign-in method ("google" popup or "password" form). */
  authMethod: AuthMethod;
};

const AuthContext = createContext<AuthState | null>(null);

// A synthetic identity used only in DEMO mode (no API configured).
const DEMO_USER: MeResponse = {
  uid: "demo",
  email: "demo@vera.local",
  role: "operator",
  environment: "demo",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(IS_DEMO ? DEMO_USER : null);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [error, setError] = useState<ApiError | Error | null>(null);

  // Register the token provider so the API client can attach the Bearer token
  // at request time (cloud only — returns null in local/dev, so no header).
  useEffect(() => {
    setTokenProvider(FIREBASE_ENABLED ? getIdToken : null);
    return () => setTokenProvider(null);
  }, []);

  // F22: any business-route 401 (revoked token, allowlist change mid-session)
  // clears the session so RequireAuth redirects to /login — instead of every
  // screen looping on a non-actionable auth error. Cloud-only: in local mode the
  // server never 401s, and DEMO makes no real calls.
  useEffect(() => {
    if (!FIREBASE_ENABLED) {
      return undefined;
    }
    setOnUnauthorized(() => setUser(null));
    return () => setOnUnauthorized(null);
  }, []);

  const refreshMe = useCallback(async () => {
    if (IS_DEMO) {
      setUser(DEMO_USER);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.GET("/me", {});
      setUser(res.data ?? null);
    } catch (err: unknown) {
      // A 401 here means "not signed in" — expected in cloud before sign-in.
      if (isApiError(err) && err.isAuth) {
        setUser(null);
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial identity check. In cloud mode, also re-check on Firebase auth state
  // changes (sign-in/out) so the token is present before getMe runs.
  useEffect(() => {
    if (IS_DEMO) {
      setLoading(false);
      return;
    }
    if (FIREBASE_ENABLED && firebaseAuth) {
      const unsub = onAuthStateChanged(firebaseAuth, () => {
        void refreshMe();
      });
      return unsub;
    }
    // Local/dev: tokenless getMe.
    void refreshMe();
    return undefined;
  }, [refreshMe]);

  const signIn = useCallback(
    async (credentials?: { email: string; password: string }) => {
      if (AUTH_METHOD === "password") {
        if (!credentials) {
          throw new Error("Ingrese su correo y contraseña.");
        }
        await passwordSignIn(credentials.email, credentials.password);
      } else {
        await googleSignIn();
      }
      // onAuthStateChanged will re-run getMe with the token attached.
    },
    [],
  );

  const signOut = useCallback(async () => {
    await googleSignOut();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      role: user?.role ?? null,
      loading,
      error,
      signIn,
      signOut,
      canSignIn: FIREBASE_ENABLED,
      authMethod: AUTH_METHOD,
    }),
    [user, loading, error, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
